import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { api, baixarBlob, mensagemDoErro, qs, salvarBlob } from '../api';
import { dataCurta, diaIso, plural } from '../format';
import { useApi, useDebounce } from '../hooks';
import { useMunicipios, useSecretarias } from '../lookups';
import { pode } from '../permissoes';
import { usuario } from '../state';
import type { Acao, Projeto } from '../types';
import { Barra, Carregando, avisar } from '../ui';
import { AcaoDetalheModal } from './AcaoDetalheModal';
import { AcaoFormModal } from './AcaoFormModal';
import { ChipStatus, STATUS_OPCOES, useRecarga } from './comum';
import { ImportarCsvModal } from './ImportarCsvModal';
import { ROTULO_STATUS } from '../format';

type Filtros = { q: string; secretariaId: string; projetoId: string; status: string; atrasada: string; municipio: string };
const VAZIO: Filtros = { q: '', secretariaId: '', projetoId: '', status: '', atrasada: '', municipio: '' };
const POR_PAGINA = 30;

export function AcoesPage() {
  const podeEscrever = pode(usuario.value?.perfil, 'acao:write');
  const { lista: secretarias } = useSecretarias();
  const { lista: municipios } = useMunicipios();
  const projetos = useApi<Projeto[]>('/projetos?ativo=true');

  const [f, setF] = useState<Filtros>(VAZIO);
  const q = useDebounce(f.q, 300);
  const filtro = useMemo(() => ({ ...f, q }), [f.secretariaId, f.projetoId, f.status, f.atrasada, f.municipio, q]);

  const [itens, setItens] = useState<Acao[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [recarga, recarregar] = useRecarga();
  const geracao = useRef(0);

  const [aberta, setAberta] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [importando, setImportando] = useState(false);

  async function carregar(depoisDe: string | null) {
    const minha = ++geracao.current;
    setCarregando(true);
    setErro('');
    try {
      const r = await api<{ itens: Acao[]; proximoCursor: string | null }>(`/acoes${qs({ ...filtro, cursor: depoisDe, limite: POR_PAGINA })}`);
      if (minha !== geracao.current) return; // chegou depois de um filtro mais novo: descarta
      setItens((atual) => (depoisDe ? [...atual, ...r.itens] : r.itens));
      setCursor(r.proximoCursor);
    } catch (e) {
      if (minha === geracao.current) setErro(mensagemDoErro(e));
    } finally {
      if (minha === geracao.current) setCarregando(false);
    }
  }

  useEffect(() => {
    void carregar(null);
  }, [filtro, recarga]);

  const projetosDaSecretaria = (projetos.dados ?? []).filter((p) => !f.secretariaId || p.secretariaId === f.secretariaId);
  const temFiltro = Object.values(f).some(Boolean);

  async function exportar() {
    try {
      const blob = await baixarBlob(`/acoes/exportar.csv${qs({ ...filtro })}`);
      salvarBlob(blob, `acoes-${diaIso()}.csv`);
    } catch (e) {
      avisar(mensagemDoErro(e), 'erro');
    }
  }

  const set = (k: keyof Filtros) => (e: Event) => {
    const v = (e.currentTarget as HTMLInputElement).value;
    setF((a) => ({ ...a, [k]: v, ...(k === 'secretariaId' ? { projetoId: '' } : {}) }));
  };

  return (
    <main class="pagina">
      <header class="pagina-topo">
        <div>
          <h1>Ações</h1>
          <p class="dica">Acompanhamento das ações de cada secretaria nos municípios da Paraíba.</p>
        </div>
        <div class="pagina-acoes">
          <button type="button" class="botao" onClick={() => void exportar()}>Exportar CSV</button>
          {podeEscrever && <button type="button" class="botao" onClick={() => setImportando(true)}>Importar CSV</button>}
          {podeEscrever && <button type="button" class="botao primario" onClick={() => setCriando(true)}>+ Nova ação</button>}
        </div>
      </header>

      <section class="barra-filtros" aria-label="Filtros">
        <label class="campo"><span>Buscar no título</span><input type="search" value={f.q} onInput={set('q')} placeholder="Ex.: escola, praça…" /></label>
        {secretarias.length > 1 && (
          <label class="campo"><span>Secretaria</span>
            <select value={f.secretariaId} onChange={set('secretariaId')}>
              <option value="">Todas</option>
              {secretarias.map((s) => <option key={s.id} value={s.id}>{s.sigla}</option>)}
            </select>
          </label>
        )}
        <label class="campo"><span>Projeto</span>
          <select value={f.projetoId} onChange={set('projetoId')}>
            <option value="">Todos</option>
            {projetosDaSecretaria.map((p) => <option key={p.id} value={p.id}>{p.titulo}</option>)}
          </select>
        </label>
        <label class="campo"><span>Município</span>
          <select value={f.municipio} onChange={set('municipio')}>
            <option value="">Todos</option>
            {municipios.map((m) => <option key={m.cod} value={m.cod}>{m.nome}</option>)}
          </select>
        </label>
        <label class="campo"><span>Situação</span>
          <select value={f.status} onChange={set('status')}>
            <option value="">Todas</option>
            {STATUS_OPCOES.map((s) => <option key={s} value={s}>{ROTULO_STATUS[s]}</option>)}
          </select>
        </label>
        <label class="campo"><span>Prazo</span>
          <select value={f.atrasada} onChange={set('atrasada')}>
            <option value="">Todos</option>
            <option value="true">Só atrasadas</option>
            <option value="false">Sem atraso</option>
          </select>
        </label>
        {temFiltro && <button type="button" class="link" onClick={() => setF(VAZIO)}>Limpar filtros</button>}
      </section>

      {erro && <p class="erro caixa-erro" role="alert">{erro} <button type="button" class="link" onClick={() => void carregar(null)}>Tentar de novo</button></p>}

      <div class="tabela-rolagem">
        <table class="tabela">
          <caption class="so-leitor">Lista de ações</caption>
          <thead>
            <tr><th scope="col">Ação</th><th scope="col">Secretaria</th><th scope="col">Município</th><th scope="col">Situação</th><th scope="col" class="col-prog">Progresso</th><th scope="col">Prazo</th></tr>
          </thead>
          <tbody>
            {itens.map((a) => (
              <tr key={a.id} class={a.status === 'CANCELADA' ? 'cancelada' : ''}>
                <td>
                  <button type="button" class="link-linha" onClick={() => setAberta(a.id)}>{a.titulo}</button>
                  <small class="sub">{a.projeto}</small>
                </td>
                <td>{a.secretaria}</td>
                <td>{a.municipio}</td>
                <td class="chips"><ChipStatus status={a.status} atrasada={a.atrasada} /></td>
                <td class="col-prog"><div class="prog-celula"><Barra pct={a.progressoPct} /><span>{a.progressoPct}%</span></div></td>
                <td>{dataCurta(a.dataFimPrevista, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {carregando && itens.length === 0 && <Carregando />}
        {!carregando && !erro && itens.length === 0 && (
          <div class="vazio-grande">
            <p>{temFiltro ? 'Nenhuma ação com esses filtros.' : 'Nenhuma ação cadastrada ainda.'}</p>
            {podeEscrever && !temFiltro && <button type="button" class="botao primario" onClick={() => setCriando(true)}>Cadastrar a primeira ação</button>}
          </div>
        )}
      </div>

      <footer class="rodape-lista">
        <span class="dica">{plural(itens.length, 'ação exibida', 'ações exibidas')}{cursor ? ' (há mais)' : ''}</span>
        {cursor && <button type="button" class="botao" disabled={carregando} onClick={() => void carregar(cursor)}>{carregando ? 'Carregando…' : 'Carregar mais'}</button>}
      </footer>

      {aberta && <AcaoDetalheModal id={aberta} onFechar={() => setAberta(null)} onMudou={recarregar} />}
      {criando && <AcaoFormModal onFechar={() => setCriando(false)} onSalvo={(a) => { setCriando(false); recarregar(); setAberta(a.id); }} />}
      {importando && <ImportarCsvModal onFechar={() => setImportando(false)} onImportado={recarregar} />}
    </main>
  );
}
